import { showNotification } from '@mantine/notifications';
import { useApiBulkImport } from '@repo/api-codegen/v2';
import { unparse } from 'papaparse';
import { useState } from 'react';
import { ColumnMappingModal } from './ColumnMappingModal/ColumnMappingModal';
import { ImportResultsDialog } from './ImportResultsDialog';

export type ColumnMappingRecord = { csvColumns: string; secodaFields: string };

interface ImportModalProps {
	fileData: Record<string, string>[];
	selectedFile: File | null;
	setSelectedFile: (file: File | null) => void;
	selectedColumns: {
		selectedRecords: ColumnMappingRecord[];
		lastSelectedIndex: number | null;
	};
	setSelectedColumns: (
		selectedColumns:
			| {
					selectedRecords: ColumnMappingRecord[];
					lastSelectedIndex: number | null;
			  }
			| ((prev: {
					selectedRecords: ColumnMappingRecord[];
					lastSelectedIndex: number | null;
			  }) => {
					selectedRecords: ColumnMappingRecord[];
					lastSelectedIndex: number | null;
			  })
	) => void;
	columnMapping: Record<string, string>;
	setColumnMapping: (
		columnMapping:
			| Record<string, string>
			| ((prev: Record<string, string>) => Record<string, string>)
	) => void;
	showMappingModal: boolean;
	setShowMappingModal: (showMappingModal: boolean) => void;
	targetColumns: { value: string; label: string }[];
}

export function ImportModal({
	fileData,
	selectedFile,
	setSelectedFile,
	selectedColumns,
	setSelectedColumns,
	columnMapping,
	setColumnMapping,
	showMappingModal,
	setShowMappingModal,
	targetColumns,
}: ImportModalProps) {
	const [showResultsDialog, setShowResultsDialog] = useState(false);
	const [importResults, setImportResults] = useState<{
		resultsFileUrl?: string | null;
		jobId?: string;
	}>({});

	const bulkImportMutation = useApiBulkImport({
		onSuccess: (response) => {
			// Show results dialog immediately and let it handle polling
			setImportResults({
				resultsFileUrl: response.results_file_url,
				jobId: response.id,
			});
			setShowResultsDialog(true);
		},
		onError: () => {
			showNotification({
				title: 'Import Failed',
				message: 'There was an error processing your import file',
				color: 'red',
			});
		},
	});

	const handleContinue = async () => {
		if (!selectedFile || !fileData.length) return;

		const selectedColumnsSet = new Set(
			selectedColumns.selectedRecords.map((record) => record.csvColumns)
		);
		const mappedData = fileData.map((row) => {
			const newRow: Record<string, string> = {};
			Object.entries(row).forEach(([originalCol, value]) => {
				if (selectedColumnsSet.has(originalCol)) {
					const mappedCol = columnMapping[originalCol];
					if (mappedCol !== 'select') {
						newRow[mappedCol] = value;
					}
				}
			});
			return newRow;
		});

		const mappedCsvString = unparse(mappedData);
		const mappedFile = new File([mappedCsvString], selectedFile.name, {
			type: 'text/csv',
		});

		if (!mappedFile) return;

		// Create FormData for file upload
		const formData = new FormData();
		formData.append('file', mappedFile);

		// Use the bulk import hook with FormData
		bulkImportMutation.mutateAsync({
			body: formData as unknown as { file: Blob },
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});

		setShowMappingModal(false);
		setSelectedFile(null);
	};

	return (
		<>
			<ColumnMappingModal
				opened={showMappingModal}
				onClose={() => {
					setShowMappingModal(false);
					setSelectedFile(null);
				}}
				onContinue={handleContinue}
				fileColumnNames={fileData.length > 0 ? Object.keys(fileData[0]) : []}
				targetColumns={targetColumns}
				columnMapping={columnMapping}
				setColumnMapping={setColumnMapping}
				selectedColumns={selectedColumns}
				setSelectedColumns={setSelectedColumns}
			/>

			<ImportResultsDialog
				opened={showResultsDialog}
				onClose={() => setShowResultsDialog(false)}
				resultsFileUrl={importResults.resultsFileUrl}
				jobId={importResults.jobId}
			/>
		</>
	);
}
